import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { Plus, Trash2, Loader } from 'lucide-react';
import OrderLineItemsEditor, {
  emptyLineItem,
  calcOrderTotalFromItems,
  lineItemsToApiPayload,
  formatLineItemsSummary,
} from './OrderLineItemsEditor';
import { istToday, formatIstDate } from '../utils/formatIst';
import { formatOrderStatus } from '../utils/formatOrderStatus';
import { useAuth } from '../context/AuthContext';
import {
  buildOrderRepOptions,
  getDefaultOrderRepId,
  isRepUser,
  resolveOrderRepId,
  resolveOrderRepName,
} from '../utils/orderDefaults';
import {
  ORDER_SOURCE_STYLES,
  SALES_CHANNEL_STYLES,
  normalizeOrderSource,
  resolveOrderSourceLabel,
  resolveSalesChannelLabel,
} from '../utils/orderSource';

const CustomerOrdersPanel = ({ customerId, customerName, reps, onClose }) => {
  const { user } = useAuth();
  const repOptions = React.useMemo(() => buildOrderRepOptions(reps, user), [reps, user]);
  const defaultRepId = React.useMemo(
    () => getDefaultOrderRepId(user, repOptions),
    [user, repOptions]
  );
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showNewOrderForm, setShowNewOrderForm] = useState(false);
  const [newOrder, setNewOrder] = useState({
    rep_id: '',
    order_date: istToday(),
    product_details: '',
    status: 'pending',
    items: [emptyLineItem()],
  });

  const blankNewOrder = () => ({
    rep_id: defaultRepId,
    order_date: istToday(),
    product_details: '',
    status: 'pending',
    items: [emptyLineItem()],
  });

  useEffect(() => {
    if (!showNewOrderForm || !defaultRepId) return;
    setNewOrder((prev) => (prev.rep_id ? prev : { ...prev, rep_id: defaultRepId }));
  }, [showNewOrderForm, defaultRepId]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getOrdersByCustomer(customerId);
      setOrders(response.data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleCreateOrder = async (e) => {
    e.preventDefault();

    const repId = resolveOrderRepId(user, newOrder.rep_id, repOptions);
    if (!repId) {
      setError('Sales rep is required');
      return;
    }

    const apiItems = lineItemsToApiPayload(newOrder.items);
    if (apiItems.length === 0) {
      setError('Add at least one line item');
      return;
    }

    try {
      setError(null);

      await adminAPI.createOrder({
        customer_id: customerId,
        rep_id: repId,
        order_amount: calcOrderTotalFromItems(newOrder.items),
        order_date: newOrder.order_date,
        product_details: newOrder.product_details || null,
        required_date: null,
        boom_pump_amount: null,
        status: newOrder.status,
        items: apiItems,
      });

      setSuccess('Order created successfully!');
      setNewOrder(blankNewOrder());
      setShowNewOrderForm(false);
      await fetchOrders();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await adminAPI.deleteOrder(orderId);
        setSuccess('Order deleted successfully!');
        await fetchOrders();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete order');
      }
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const orderTotalPreview = calcOrderTotalFromItems(newOrder.items);

  return (
    <div className="admin-page bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4 min-w-0 max-w-full">
      <div className="admin-page-header">
        <h4 className="text-lg font-semibold text-gray-900 admin-page-header-title break-words">Orders for {customerName}</h4>
        <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 shrink-0">✕</button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
      )}

      <div>
        <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span>Previous Orders</span>
          {orders.length > 0 && (
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">{orders.length}</span>
          )}
        </h5>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center text-gray-600">
            No previous orders for this customer
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {CURRENCY.SYMBOL}{parseFloat(order.order_amount).toFixed(2)} {CURRENCY.CODE}
                      {order.items?.length ? (
                        <span className="text-gray-500 font-normal text-sm ml-2">({order.items.length} items)</span>
                      ) : null}
                    </p>
                    <p className="text-sm text-gray-600">
                      {resolveOrderRepName(order, reps)}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${ORDER_SOURCE_STYLES[normalizeOrderSource(order.order_source)] || 'bg-gray-100 text-gray-700'}`}>
                        {resolveOrderSourceLabel(order)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${SALES_CHANNEL_STYLES[order.sales_channel || (order.is_direct_sale ? 'direct' : 'rep')] || 'bg-gray-100 text-gray-700'}`}>
                        {resolveSalesChannelLabel(order)}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(order.status)}`}>
                    {formatOrderStatus(order.status)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  Order Date: {formatIstDate(order.order_date)}
                </div>
                {order.items?.length ? (
                  <ul className="text-sm text-gray-700 mb-2 list-disc ml-4 space-y-0.5">
                    {order.items.map((item) => (
                      <li key={item.id}>
                        {item.product_name} — {parseFloat(item.quantity)} {item.volume_unit || 'm3'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-700 mb-1">{formatLineItemsSummary(order)}</p>
                )}
                {order.product_details && (
                  <p className="text-sm text-gray-600 mb-2 italic">{order.product_details}</p>
                )}
                <button
                  type="button"
                  onClick={() => handleDeleteOrder(order.id)}
                  className="text-red-600 hover:text-red-700 text-sm font-semibold flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <button
          type="button"
          onClick={() => {
            if (showNewOrderForm) {
              setShowNewOrderForm(false);
            } else {
              setNewOrder(blankNewOrder());
              setShowNewOrderForm(true);
            }
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2 w-full justify-center"
        >
          <Plus className="w-4 h-4" />
          {showNewOrderForm ? 'Cancel New Order' : 'Create New Order'}
        </button>

        {showNewOrderForm && (
          <form onSubmit={handleCreateOrder} className="mt-4 space-y-4 bg-white rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sales Rep *</label>
                {isRepUser(user) && defaultRepId ? (
                  <input
                    type="text"
                    readOnly
                    value={user?.name || repOptions.find((r) => String(r.id) === defaultRepId)?.name || 'You'}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800"
                  />
                ) : (
                <select
                  value={newOrder.rep_id}
                  onChange={(e) => setNewOrder({ ...newOrder, rep_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                >
                  <option value="">-- Choose sales rep --</option>
                  {repOptions.map((rep) => (
                    <option key={rep.id} value={String(rep.id)}>
                      {rep.is_direct_order ? `${rep.name} (no rep credit)` : rep.name}
                    </option>
                  ))}
                </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Order Date *</label>
                <input
                  type="date"
                  value={newOrder.order_date}
                  onChange={(e) => setNewOrder({ ...newOrder, order_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={newOrder.status}
                  onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="flex items-end">
                <p className="text-sm font-semibold text-blue-900 pb-2">
                  Total: {CURRENCY.SYMBOL}{orderTotalPreview.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <OrderLineItemsEditor
              items={newOrder.items}
              onChange={(items) => setNewOrder({ ...newOrder, items })}
            />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Order notes</label>
              <textarea
                value={newOrder.product_details}
                onChange={(e) => setNewOrder({ ...newOrder, product_details: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Delivery instructions"
                rows="2"
              />
            </div>

            <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition w-full">
              Create Order
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerOrdersPanel;
